import React, { useMemo } from 'react';

import { ShoppingOutlined, SwapOutlined } from '@ant-design/icons';
import { formatDateTime } from '@fastlessjs/backoffice-layer/dist/utils/dateFormat';
import { List, Tag, Typography } from 'antd';

import type { DashboardOrder } from '../hooks/useDashboardOrders';
import type { DashboardReturn } from '../hooks/useDashboardReturns';
import type { EnterpriseResolver } from '../schemas';

type Props = {
  orders?: DashboardOrder[];
  returns?: DashboardReturn[];
  /** Maximum items shown (default 24). */
  limit?: number;
  resolveEnterprise?: EnterpriseResolver;
};

type ActivityItem = {
  key: string;
  kind: 'order' | 'return';
  title: string;
  subtitle: string;
  createdAt: string | undefined;
};

export const RecentActivity = ({
  orders = [],
  returns = [],
  limit = 24,
  resolveEnterprise,
}: Props) => {
  const items = useMemo(() => {
    const resolve = (id?: number | null) => {
      if (resolveEnterprise) return resolveEnterprise(id);
      if (id != null) return `#${id}`;
      return '—';
    };

    // Cap each source to half the limit before merging so neither type can
    // crowd out the other when one set is much larger or more recent.
    const half = Math.ceil(limit / 2);
    const ordersSlice = orders.slice(0, half);
    const returnsSlice = returns.slice(0, half);

    const merged: ActivityItem[] = [];

    ordersSlice.forEach((o, i) => {
      merged.push({
        key: `o-${o.orderId ?? i}`,
        kind: 'order',
        title: `Order ${o.extOrderId || o.orderId || '—'}`,
        subtitle: [o.clientEmail || o.clientRef, resolve(o.enterpriseId)]
          .filter(Boolean)
          .join(' · '),
        // eslint-disable-next-line no-underscore-dangle
        createdAt: o._createdAt,
      });
    });

    returnsSlice.forEach((s, i) => {
      merged.push({
        key: `s-${s.suiviId ?? i}`,
        kind: 'return',
        title: `Return #${s.suiviId ?? '—'}`,
        subtitle: [
          s.clientUserName || s.clientUserEmail,
          s.createdByRole,
          resolve(s.enterpriseId),
        ]
          .filter(Boolean)
          .join(' · '),
        // eslint-disable-next-line no-underscore-dangle
        createdAt: s._createdAt,
      });
    });

    merged.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });

    return merged.slice(0, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, returns, limit, resolveEnterprise]);

  return (
    <List
      className="dashboard-recent-activity"
      itemLayout="horizontal"
      dataSource={items}
      locale={{ emptyText: 'No recent activity in this period' }}
      renderItem={(item) => (
        <List.Item>
          <List.Item.Meta
            avatar={
              item.kind === 'order' ? (
                <ShoppingOutlined className="dashboard-recent-activity__avatar dashboard-recent-activity__avatar--order" />
              ) : (
                <SwapOutlined className="dashboard-recent-activity__avatar dashboard-recent-activity__avatar--suivi" />
              )
            }
            title={
              <span>
                {item.title}{' '}
                {item.kind === 'return' ? (
                  <Tag color="orange">Return</Tag>
                ) : (
                  <Tag color="blue">Order</Tag>
                )}
              </span>
            }
            description={
              <>
                <Typography.Text type="secondary">{item.subtitle}</Typography.Text>
                <div>
                  <Typography.Text
                    type="secondary"
                    className="dashboard-recent-activity__timestamp"
                  >
                    {formatDateTime(item.createdAt)}
                  </Typography.Text>
                </div>
              </>
            }
          />
        </List.Item>
      )}
    />
  );
};
